import { forwardRef, type ChangeEvent, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { labelledTokens } from '@nest-recruit/shared';
import styles from './PillTextField.module.css';

/**
 * ADMIN-09's subject and message (Admin redesign batch 2, Option A: "variables show as blue pills, so nobody types
 * {{braces}} by hand"). A plain text box underneath, so typing, selecting, pasting and undo are the browser's own;
 * behind it the same words are drawn again with each `{{Job title}}` as a pill, letter for letter in the same place.
 * Both share one grid cell, so the box grows with the words and the pills can never drift from the text.
 *
 * A pill this email can't fill is drawn red. The text is in labelled form (`toLabelledText`); the editor turns it
 * back into variables only when it saves or previews.
 */
export const PillTextField = forwardRef<
  HTMLTextAreaElement,
  {
    readonly id: string;
    readonly value: string;
    readonly palette: readonly string[];
    readonly onChange: (value: string) => void;
    readonly onFocus?: (event: FocusEvent<HTMLTextAreaElement>) => void;
    /** The subject: one line, Enter does nothing and a pasted line break becomes a space. */
    readonly isSingleLine?: boolean;
    readonly isInvalid?: boolean;
    readonly isReadOnly?: boolean;
    readonly maxLength: number;
    readonly describedBy?: string;
  }
>(function PillTextField({ id, value, palette, onChange, onFocus, isSingleLine = false, isInvalid = false, isReadOnly = false, maxLength, describedBy }, ref): ReactNode {
  const change = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    onChange(isSingleLine ? event.target.value.replace(/\r?\n/g, ' ') : event.target.value);
  };
  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    // Ctrl+Enter still reaches the form, which saves.
    if (isSingleLine && event.key === 'Enter' && !event.ctrlKey && !event.metaKey) event.preventDefault();
  };
  return (
    <div className={styles.wrap} data-single={isSingleLine || undefined} data-invalid={isInvalid || undefined} data-readonly={isReadOnly || undefined}>
      <div className={styles.backdrop} aria-hidden>
        {pieces(value, palette)}
        {/* A last line break needs something after it to take up its line. */}
        {' '}
      </div>
      <textarea
        ref={ref}
        id={id}
        className={styles.input}
        value={value}
        rows={1}
        maxLength={maxLength}
        spellCheck
        readOnly={isReadOnly}
        aria-invalid={isInvalid || undefined}
        aria-describedby={describedBy}
        onChange={change}
        onKeyDown={keyDown}
        onFocus={onFocus}
      />
    </div>
  );
});

function pieces(text: string, palette: readonly string[]): ReactNode[] {
  const out: ReactNode[] = [];
  let at = 0;
  for (const token of labelledTokens(text, palette)) {
    if (token.start > at) out.push(text.slice(at, token.start));
    out.push(
      <mark key={token.start} className={styles.pill} data-known={token.isKnown || undefined}>
        {token.text}
      </mark>,
    );
    at = token.end;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}
